import { useEffect, useState } from 'react';

import { payerApiOrigin } from './payerApiOrigin';

/**
 * AI connections (MCP) configuration for the webapp.
 *
 * Whether MCP is LIVE is one switch, held on the payer side: the `enabled` flag of the
 * introspection webhookConfig. Off, the payer refuses every MCP call AND this webapp hides
 * AI connections; on, both come back. Browsers never read webhookConfig (it holds the key),
 * so the webapp asks the payer's public `GET /api/oauth/status` once per page load and
 * remembers the answer for the session. The routes stay reachable by URL regardless.
 *
 * `REACT_APP_MCP_LIVE=true` is a local-development override only (no payer call).
 */

/** The MCP server URL people paste into Claude. Configuration, never a guess. */
export const MCP_SERVER_URL = process.env.REACT_APP_MCP_URL || 'https://mcp.strikepay.co/mcp';

export const CONNECTOR_NAME = 'Strikepay';

/** Only the literal string "true" forces live locally; anything else defers to the payer. */
export function isMcpLiveOverride(raw) {
  return raw === 'true';
}

let statusPromise = null;

/** Resets the session cache (tests only). */
export function resetMcpStatusForTests() {
  statusPromise = null;
}

/** Asks the payer whether MCP is live; false on any failure, remembered for the session. */
export function fetchMcpLive() {
  if (isMcpLiveOverride(process.env.REACT_APP_MCP_LIVE)) {
    return Promise.resolve(true);
  }
  if (!statusPromise) {
    try {
      const base = payerApiOrigin();
      statusPromise = fetch(`${base}/api/oauth/status`)
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => !!(body && body.live === true))
        .catch(() => false);
    } catch (e) {
      statusPromise = Promise.resolve(false);
    }
  }
  return statusPromise;
}

/** `null` while unknown, then true or false. Never throws, never flickers once answered. */
export function useMcpLive() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    let active = true;
    fetchMcpLive().then((value) => {
      if (active) {
        setLive(value);
      }
    });
    return () => { active = false; };
  }, []);
  return live;
}
